import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  useAnyProjectPermission,
  useEffectivePermissions,
  usePermission,
} from './PermissionsContext';
import type { PermissionLevel, PermissionResource } from './permission-catalog';
import PermissionDeniedModal from './PermissionDeniedModal';
import type { PermissionPhraseOverrides } from './permission-messages';

export interface GuardedActionInput extends PermissionPhraseOverrides {
  resource: PermissionResource;
  level: PermissionLevel;
  /** Board content: also consult this project's grants (most-permissive-wins,
   *  same as `usePermission`). Omit for a team-scope-only requirement — e.g.
   *  creating a project, which a project-scoped grant can never authorize. */
  projectId?: number | string | null;
  /** Mirror a route gated by `requireAnyProjectPermission`: the grant counts
   *  at team scope OR on any ONE project. Set this only when that route's
   *  snapshot line carries `[anyProject]`; on an ordinary team-scoped route it
   *  would show a control whose call the server then refuses. `projectId`
   *  names one specific project and is ignored when this is set. */
  anyProject?: boolean;
  /** What the button actually does, when the caller may do it. */
  onAllowed: () => void;
  /** Test id for the denial modal, when one screen has several. */
  testId?: string;
}

export interface GuardedAction {
  /** True when the caller holds the permission. Exposed for copy that needs to
   *  read differently, NOT for deciding whether to render the control. */
  allowed: boolean;
  /** Hand this to the control. Runs `onAllowed`, or opens the denial modal. */
  onClick: (event?: React.MouseEvent) => void;
  /** MUST be rendered. `GuardedActionButton` does it for you. */
  denialModal: React.ReactNode;
}

/**
 * One decision point for "always render the control; explain on click".
 *
 * The old shape was `canEdit && <Button onClick={doIt}/>`: two independent
 * things a developer had to keep in agreement, where dropping the `canEdit &&`
 * left a button that called `doIt` unguarded. Here the permission check and
 * the handler are the same expression — you cannot obtain an `onClick` from
 * this hook without the check having been applied to it.
 *
 * Prefer `GuardedActionButton` (below) which also renders `denialModal` for
 * you; reach for the raw hook only when the control is not a single element.
 *
 * Not a security boundary: `requirePermission` on the route is. This decides
 * which of two things a click does in the browser, and a browser is not where
 * access is decided. What it buys is that the refusal is explained before the
 * user invests in a form, instead of arriving as a 403 afterwards.
 */
export function useGuardedAction({
  resource,
  level,
  projectId,
  anyProject,
  verb,
  subject,
  onAllowed,
  testId,
}: GuardedActionInput): GuardedAction {
  // Both hooks run every render — a hook called conditionally would break the
  // call order the moment `anyProject` changed.
  const scoped = usePermission(resource, level, projectId);
  const acrossProjects = useAnyProjectPermission(resource, level);
  const allowed = anyProject ? acrossProjects : scoped;
  const { isLoading } = useEffectivePermissions();
  const [denied, setDenied] = useState(false);
  /**
   * A click that arrived before the permissions fetch settled.
   *
   * `usePermission` returns false while loading — it cannot distinguish "not
   * yet known" from "denied" — so acting on it here would REFUSE a caller who
   * actually holds the grant, purely because they clicked early. That is not a
   * hypothetical: it turned three e2e shards red, and on a slow connection a
   * real user would hit it. So a click during the load window is parked and
   * replayed against the real answer.
   */
  const pendingClick = useRef(false);

  const decide = useCallback(
    (mayAct: boolean) => {
      if (mayAct) onAllowed();
      else setDenied(true);
    },
    [onAllowed],
  );

  useEffect(() => {
    if (isLoading || !pendingClick.current) return;
    pendingClick.current = false;
    decide(allowed);
  }, [isLoading, allowed, decide]);

  const onClick = useCallback(
    (event?: React.MouseEvent) => {
      // Rows that are themselves clickable (a project tile, a role card) sit
      // under these buttons; without this, denying the action would also
      // navigate, and the modal would open on a page that is unmounting.
      event?.stopPropagation();
      if (isLoading) {
        pendingClick.current = true;
        return;
      }
      decide(allowed);
    },
    [allowed, decide, isLoading],
  );

  return {
    allowed,
    onClick,
    denialModal: (
      <PermissionDeniedModal
        isOpen={denied}
        onClose={() => setDenied(false)}
        resource={resource}
        level={level}
        verb={verb}
        subject={subject}
        {...(testId ? { testId } : {})}
      />
    ),
  };
}

export interface GuardedCallbackInput<
  A extends unknown[],
  R = void,
> extends Omit<GuardedActionInput, 'onAllowed'> {
  /** What the control actually does, with its own arguments, when allowed. */
  onAllowed: (...args: A) => R;
}

export interface GuardedCallback<A extends unknown[], R = void> {
  allowed: boolean;
  /**
   * Hand this to the control in place of the real handler. Returns whatever
   * `onAllowed` returned, or `undefined` when the check refused — so a caller
   * whose contract is `=> Promise<void>` can forward the real promise instead
   * of a resolved one. That distinction is load-bearing: several approval
   * cards `await onDecide(...)` to hold their own spinner, and handing them an
   * already-resolved promise would clear it before the request finished.
   */
  run: (...args: A) => R | undefined;
  /** MUST be rendered, once, somewhere in the component. */
  denialModal: React.ReactNode;
}

/**
 * `useGuardedAction` for controls that are NOT buttons.
 *
 * A board has plenty of mutating controls with no button to hang
 * `GuardedActionButton` on: the epic/sprint/column `<select>`s on a card, the
 * label name/colour inputs that write on blur, the "celebrate here" checkbox,
 * a drag that ends in a card move. Those handlers carry arguments (the picked
 * id, the typed text, the drop target), which `onAllowed: () => void` cannot,
 * so the argument is parked in a ref and replayed once the check has passed.
 *
 * Same contract as the hook it wraps: `run` cannot be obtained without the
 * check having been applied to it, denied never reaches `onAllowed`, and no
 * request is made. One instance can serve every control in a component that
 * shares the same (resource, level, phrase) — the modal is rendered once.
 */
export function useGuardedCallback<A extends unknown[], R = void>({
  onAllowed,
  ...guard
}: GuardedCallbackInput<A, R>): GuardedCallback<A, R> {
  const argsRef = useRef<A | null>(null);
  const resultRef = useRef<R | undefined>(undefined);
  const action = useGuardedAction({
    ...guard,
    onAllowed: () => {
      const args = argsRef.current;
      argsRef.current = null;
      if (args) resultRef.current = onAllowed(...args);
    },
  });
  const run = useCallback(
    (...args: A) => {
      argsRef.current = args;
      // Cleared first: `onClick` is synchronous, so after it either this run
      // set a fresh result or the check refused and there is none. Without
      // the reset a denied run would hand back the PREVIOUS run's promise.
      resultRef.current = undefined;
      action.onClick();
      return resultRef.current;
    },
    [action],
  );
  return { allowed: action.allowed, run, denialModal: action.denialModal };
}

export interface GuardedActionButtonProps extends GuardedActionInput {
  children: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
  title?: string;
  'aria-label'?: string;
  'data-testid'?: string;
  disabled?: boolean;
  /** Render as something other than a plain `<button>` — pass `AsyncButton`
   *  where a pending state is needed. Anything accepting button props works. */
  as?: React.ElementType;
  /** Forwarded to `AsyncButton` when `as={AsyncButton}`. */
  loading?: boolean;
  loadingText?: React.ReactNode;
}

/**
 * A button that is always rendered and always guarded.
 *
 * This is the form to reach for, because the failure mode of the hook — render
 * the control, forget `denialModal`, ship a button that does nothing when
 * denied — is not expressible here: the component owns both halves. There is
 * no prop that makes it render the control without the check.
 */
export const GuardedActionButton: React.FC<GuardedActionButtonProps> = ({
  children,
  className,
  style,
  title,
  'aria-label': ariaLabel,
  'data-testid': dataTestId,
  disabled,
  as: Control = 'button',
  loading,
  loadingText,
  ...guard
}) => {
  const action = useGuardedAction(guard);
  const controlProps: Record<string, unknown> = {
    type: 'button',
    className,
    style,
    title,
    'aria-label': ariaLabel,
    'data-testid': dataTestId,
    disabled,
    onClick: action.onClick,
  };
  if (loading !== undefined) controlProps.loading = loading;
  if (loadingText !== undefined) controlProps.loadingText = loadingText;

  return (
    <>
      <Control {...controlProps}>{children}</Control>
      {action.denialModal}
    </>
  );
};

export default useGuardedAction;
